import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { CommandPalette } from '@/components/common/CommandPalette';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { PasswordChangeBanner } from '@/features/auth/PasswordChangeBanner';

const COLLAPSE_KEY = 'sms.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The authenticated shell: sidebar, header, context bar, and the routed page.
 *
 * Below 900px the sidebar is a fixed overlay drawer with a dismissing scrim;
 * at 900px and above it is part of the layout and toggles between 252px and a
 * 64px icon rail. The rail preference is remembered, the drawer is not — a
 * drawer that reopened itself on every navigation would be a bug, not a
 * convenience.
 */
export function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  const toggleCollapse = () => {
    setCollapsed((value) => {
      const next = !value;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* A dropped preference is a nuisance, never a failure. */
      }
      return next;
    });
  };

  // ⌘K / Ctrl-K toggles search; Escape closes it and the drawer.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      } else if (event.key === 'Escape') {
        setSearchOpen(false);
        setDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="flex min-h-screen bg-bg">
      <Sidebar
        open={drawerOpen}
        collapsed={collapsed}
        onNavigate={() => setDrawerOpen(false)}
        onOpenSearch={() => {
          setDrawerOpen(false);
          setSearchOpen(true);
        }}
      />

      {/* Scrim: only present below 900px, where the sidebar overlays content. */}
      {drawerOpen && (
        <div
          className="animate-fade-in fixed inset-0 z-30 min-[900px]:hidden"
          style={{ background: 'var(--sms-overlay)' }}
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          onToggleSidebar={() => setDrawerOpen((open) => !open)}
          onToggleCollapse={toggleCollapse}
          onOpenSearch={() => setSearchOpen(true)}
        />
        <PasswordChangeBanner />
        {/*
          Keyed on the path so React remounts the subtree on navigation and the
          entrance animation replays. Without the key the new page inherits the
          finished animation state and simply appears.
        */}
        <main key={location.pathname} className="animate-fade-in flex-1 px-4 pb-14 pt-[26px] lg:px-[30px]">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
